import React from 'react';
import { BoxProps, Box } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { PropsWithChildren } from 'react';
import { eventNotificationPriorityToColors } from '../../event-notifications/event-notifications';
import {
    useEventNotifications,
    useRemoveEventNotifications,
} from '../../event-notifications/use-event-notifications';
import {
    columnDefinitionNameToColumnGroup,
    columnGroupToEventNotificationNames,
} from '../column-definition.enum';
import { useQueueStore } from '../context/use-queue-store';
import { useQueueCell } from './queue-cell-context';
import { useBroadcast } from '../../common/broadcast-channel/use-broadcast';
import {
    CadNextNotification,
    CadNextNotificationPriorityEnumType,
    CadNextNotificationTypeEnumType,
} from '@mark43/cad-api';
import { highestNotificationPriorityForNotifications } from '../../event-notifications/highest-notification-priority-for-notifications';

function getNotificationTextColor(eventNotifications: CadNextNotification[]): string {
    const highestNotificationPriority =
        highestNotificationPriorityForNotifications(eventNotifications);
    if (highestNotificationPriority) {
        return eventNotificationPriorityToColors[highestNotificationPriority].notchOrText;
    }
    return 'text.primary';
}

export const BaseQueueCell = observer(
    React.forwardRef(function BaseQueueCell(
        props: PropsWithChildren<
            {
                eventNotificationPriority?: CadNextNotificationPriorityEnumType;
                alignment?: 'LEFT' | 'CENTER' | 'RIGHT';
                /** Text color while the cell shows no update notification. Defaults to inherit. */
                idleColor?: string;
            } & BoxProps
        >,
        ref: React.ForwardedRef<HTMLElement>
    ): React.ReactElement {
        const {
            alignment,
            children,
            eventNotificationPriority: notificationPriority,
            idleColor,
            ...boxProps
        } = props;
        const queueStore = useQueueStore();
        const { postMessage } = useBroadcast('dismiss-event-notifications');
        const { eventId, columnDefinitionValue } = useQueueCell();
        const removeEventNotifications = useRemoveEventNotifications();
        const queueIsShowingUpdates = queueStore.workspacePanel.queue.showUpdates;
        const notificationsForCell = useEventNotifications({
            notifications: columnDefinitionValue.eventNotificationNames,
        });
        const onClickMemo = React.useCallback(() => {
            if (
                columnDefinitionValue.eventNotificationNames &&
                notificationsForCell.length > 0 &&
                queueIsShowingUpdates
            ) {
                // This dismisses event panel notifications in the old CAD
                postMessage({
                    type: 'DISMISS_LEGACY_CAD_EVENT_PANEL_NOTIFICATIONS',
                    payload: {
                        eventId,
                        columnDefinitionName: columnDefinitionValue.name,
                    },
                });
                let notificationsToRemove: CadNextNotificationTypeEnumType[] = [];
                const columnGroup = columnDefinitionNameToColumnGroup[columnDefinitionValue.name];
                if (columnGroup) {
                    for (const CadNextNotificationTypeEnumType of columnGroupToEventNotificationNames[
                        columnGroup
                    ]) {
                        notificationsToRemove.push(CadNextNotificationTypeEnumType);
                    }
                } else {
                    notificationsToRemove = notificationsToRemove.concat(
                        columnDefinitionValue.eventNotificationNames
                    );
                }
                removeEventNotifications({
                    notifications: notificationsToRemove,
                });
            }
        }, [
            columnDefinitionValue.eventNotificationNames,
            columnDefinitionValue.name,
            notificationsForCell.length,
            queueIsShowingUpdates,
            postMessage,
            eventId,
            removeEventNotifications,
        ]);
        // Layout and density sizing come from the `base-queue-cell` classes in index.css.
        return (
            <Box
                ref={ref}
                onClick={onClickMemo}
                className="base-queue-cell"
                color={
                    queueIsShowingUpdates && notificationsForCell.length > 0
                        ? getNotificationTextColor(notificationsForCell)
                        : (idleColor ?? 'inherit')
                }
                textAlign={
                    alignment === 'CENTER' ? 'center' : alignment === 'RIGHT' ? 'right' : 'left'
                }
                fontWeight={
                    queueIsShowingUpdates && notificationsForCell.length > 0 ? 'bold' : undefined
                }
                {...boxProps}
            >
                <span className="base-queue-cell-content">{children}</span>
            </Box>
        );
    })
);
